import { useQuery, useQueryClient } from '@tanstack/react-query';
import { systemStatusService, type SystemStatus } from '../services/systemStatusService';

// Ключи для кеша
export const systemStatusKeys = {
  all: ['systemStatus'] as const,
  status: () => [...systemStatusKeys.all, 'data'] as const,
};

// Placeholder данные для мгновенного отображения UI
const placeholderSystemStatus: SystemStatus = {
  storage: {
    used: '—',
    total: '—',
  },
  load_average: {
    '1min': '—',
    '5min': '—',
    '15min': '—',
  },
  memory: {
    used: '—',
    total: '—',
  },
  uptime: '—',
  users_count: 0,
  cases_count: 0,
  tasks_count: 0,
};

/**
 * Хук для получения состояния системы
 */
export const useSystemStatus = (enabled: boolean = true) => {
  const queryClient = useQueryClient();
  const cacheKey = systemStatusKeys.status();
  
  // Получаем данные из кеша синхронно
  const cachedData = queryClient.getQueryData<SystemStatus>(cacheKey);
  
  const query = useQuery({
    queryKey: cacheKey,
    queryFn: () => systemStatusService.getStatus(),
    enabled, // Обновлять только когда включено
    staleTime: 1000 * 30, // 30 секунд - данные считаются свежими
    gcTime: 1000 * 60 * 30, // 30 минут - время хранения в кеше
    refetchInterval: enabled ? 1000 * 30 : false, // Автоматическое обновление каждые 30 секунд только если включено
    refetchIntervalInBackground: true, // Обновлять даже когда вкладка неактивна
    // Используем кешированные данные как placeholder для мгновенного отображения
    placeholderData: cachedData ?? placeholderSystemStatus,
    // Начальные данные для мгновенного отображения
    initialData: cachedData ?? placeholderSystemStatus,
    initialDataUpdatedAt: cachedData ? Date.now() - 1000 : undefined,
    // Не обновлять при монтировании, если есть свежие данные в кеше
    refetchOnMount: false,
  });
  
  // Всегда возвращаем данные (из кеша, placeholder или реальные)
  // Приоритет: реальные данные > кеш > placeholder
  const data = query.data ?? cachedData ?? placeholderSystemStatus;
  
  return {
    ...query,
    data,
    // isLoading только если нет данных вообще и идет загрузка
    isLoading: query.isLoading && !cachedData && !query.data,
  };
};
